import * as React from 'react';
import { useState, useEffect } from 'react';
import dateUtil from '../../../utils/dateUtil';
import EmployeeService from '../../employees/services/EmployeeService';
import { Employee } from '../../../hooks/useEmployeeData';
import AbsenceService, {
  Absence,
  WorkArea,
  AbsenceFormData,
  AbsenceEditFormData,
  AbsencePayload,
  AbsenceStatus
} from '../services/AbsenceService';
import AbsenceErrorHandler from '../services/AbsenceErrorHandler';
import { toast } from 'react-toastify';
import { HttpError } from '../../../core/services/HttpService';
import { FaUpload, FaDownload, FaPlus, FaCheck, FaTimes } from 'react-icons/fa';
import { downloadAbsencesTemplateXlsx } from '../api';
import DateRangeFilter, { DateRange } from '../../timetracking/components/DateRangeFilter';
import ImportAbsencesModal from './ImportAbsencesModal';
import { useUserRole } from '../../../hooks/useUserRole';
import dialog from '../../../shared/utils/dialog';
import { TableSkeleton, absenceColumns } from '../../../components/Skeleton';
import Pagination from '../../../components/Pagination';
import ActiveFiltersBar, { ActiveFilter } from '../../../components/ActiveFiltersBar';
import { AbsenceAddModal } from './AbsenceAddModal';
import { AbsenceEditModal } from './AbsenceEditModal';
import { AbsenceTable } from './AbsenceTable';
import {
  SortColumn, SortDirection,
  sortAbsences, filterAbsences, buildPayloadFromTimes,
} from './absencesUtils';
import styles from './Absences.module.css';

const DEFAULT_STATUS: Record<AbsenceStatus, boolean> = { REQUESTED: true, APPROVED: true, REJECTED: false, CANCELLED: false };

interface AbsencesProps {
  activeEmployeeId?: number | null;
  setActiveEmployeeId?: (id: number | null) => void;
  workAreas?: WorkArea[];
  selectedWAs?: number[];
  onSelectedWAsChange?: (ids: number[]) => void;
  renderHeaderControls?: boolean;
  addModalOpen?: boolean;
  onAddModalOpenChange?: (open: boolean) => void;
  importModalOpen?: boolean;
  onImportModalOpenChange?: (open: boolean) => void;
}

export default function Absences({
  activeEmployeeId: propActiveEmployeeId = null,
  setActiveEmployeeId: propSetActiveEmployeeId = null,
  workAreas: externalWorkAreas,
  selectedWAs: externalSelectedWAs,
  onSelectedWAsChange,
  renderHeaderControls = true,
  addModalOpen: externalAddModalOpen,
  onAddModalOpenChange,
  importModalOpen: externalImportModalOpen,
  onImportModalOpenChange,
}: AbsencesProps = {}): React.JSX.Element {
  const { role, workAreaIds, isManager } = useUserRole();

  const [absences, setAbsences] = useState<Absence[]>([]);
  const [internalWorkAreas, setInternalWorkAreas] = useState<WorkArea[]>([]);
  const [internalSelectedWAs, setInternalSelectedWAs] = useState<number[]>([]);
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const [statusFilter, setStatusFilter] = useState<Record<AbsenceStatus, boolean>>({
    REQUESTED: true, APPROVED: true, REJECTED: false, CANCELLED: false
  });

  const workAreas = externalWorkAreas !== undefined ? externalWorkAreas : internalWorkAreas;
  const selectedWorkAreas = externalSelectedWAs !== undefined ? externalSelectedWAs : internalSelectedWAs;
  const setSelectedWorkAreas = (ids: number[] | ((prev: number[]) => number[])) => {
    if (onSelectedWAsChange) {
      const newIds = typeof ids === 'function' ? ids(selectedWorkAreas) : ids;
      onSelectedWAsChange(newIds);
    } else {
      setInternalSelectedWAs(ids);
    }
  };
  const setWorkAreas = (was: WorkArea[]) => {
    if (externalWorkAreas === undefined) setInternalWorkAreas(was);
  };

  const [showAddModalInternal, setShowAddModalInternal] = useState<boolean>(false);
  const showAddModal = externalAddModalOpen !== undefined ? externalAddModalOpen : showAddModalInternal;
  const setShowAddModal = (open: boolean) => {
    if (onAddModalOpenChange) onAddModalOpenChange(open);
    else setShowAddModalInternal(open);
  };

  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [localActiveEmployeeId, setLocalActiveEmployeeId] = useState<number | null>(null);
  const activeEmployeeId = propActiveEmployeeId !== null ? propActiveEmployeeId : localActiveEmployeeId;
  const setActiveEmployeeId = propSetActiveEmployeeId || setLocalActiveEmployeeId;

  const [sortBy, setSortBy] = useState<SortColumn>('employee_name');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editData, setEditData] = useState<AbsenceEditFormData>({
    id: null, employee: '', absence_type: 'HOLIDAY',
    start_date: '', full_day_start: true, start_day_start_time: '09:00', start_day_end_time: '17:00',
    end_date: '', full_day_end: true, end_day_start_time: '09:00', end_day_end_time: '17:00',
    reason_note: '', requested_on: '', status: 'REQUESTED'
  });

  const [showImportModalInternal, setShowImportModalInternal] = useState<boolean>(false);
  const showImportModal = externalImportModalOpen !== undefined ? externalImportModalOpen : showImportModalInternal;
  const setShowImportModal = (open: boolean) => {
    if (onImportModalOpenChange) onImportModalOpenChange(open);
    else setShowImportModalInternal(open);
  };

  const [addData, setAddData] = useState<AbsenceFormData>({
    employee: '', absence_type: 'HOLIDAY',
    start_date: '', full_day_start: true, start_day_start_time: '09:00', start_day_end_time: '17:00',
    end_date: '', full_day_end: true, end_day_start_time: '09:00', end_day_end_time: '17:00',
    reason_note: '', requested_on: ''
  });

  useEffect(() => {
    Promise.all([loadAbsences(), loadWorkAreas(), loadEmployees()])
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setSelectedIds(new Set());
    setCurrentPage(1);
  }, [statusFilter, dateRange, activeEmployeeId, selectedWorkAreas]);

  useEffect(() => {
    if (isManager && workAreaIds.length > 0 && workAreas.length > 0 && selectedWorkAreas.length === 0) {
      setSelectedWorkAreas(workAreaIds);
    }
  }, [isManager, workAreaIds, workAreas, selectedWorkAreas.length]);

  async function loadAbsences(): Promise<void> {
    try {
      const absencesData = await AbsenceService.fetchAllAbsences();
      setAbsences(absencesData);
    } catch (error) {
      if (error instanceof HttpError) {
        if (!AbsenceErrorHandler.handleFetchError(error)) console.error('Error loading absences:', error);
      } else { console.error('Error loading absences:', error); }
    }
  }

  async function loadWorkAreas(): Promise<void> {
    try {
      const workAreasData = await AbsenceService.fetchAllWorkAreas();
      setWorkAreas(workAreasData);
    } catch (error) {
      if (error instanceof HttpError) {
        if (!AbsenceErrorHandler.handleFetchError(error)) console.error('Error loading work areas:', error);
      } else { console.error('Error loading work areas:', error); }
    }
  }

  async function loadEmployees(): Promise<void> {
    try {
      const employees = await EmployeeService.fetchAllEmployees();
      setAllEmployees(employees);
    } catch (error) { console.error('Error loading employees:', error); }
  }

  const filteredAbsences = filterAbsences(absences, selectedWorkAreas, activeEmployeeId, dateRange, statusFilter, allEmployees);

  function handleSort(col: SortColumn): void {
    if (sortBy === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  }

  async function handleApprove(absId: number): Promise<void> {
    try { await AbsenceService.approveAbsence(absId); await loadAbsences(); } catch (error) {
      if (error instanceof HttpError) { if (!AbsenceErrorHandler.handleApprovalError(error)) console.error('Approve error:', error); }
      else console.error('Approve error:', error);
    }
  }

  async function handleReject(absId: number): Promise<void> {
    try { await AbsenceService.rejectAbsence(absId); await loadAbsences(); } catch (error) {
      if (error instanceof HttpError) { if (!AbsenceErrorHandler.handleApprovalError(error)) console.error('Reject error:', error); }
      else console.error('Reject error:', error);
    }
  }

  async function handleDeleteAbs(absId: number): Promise<void> {
    const yes = await dialog.confirm('Delete Absence', 'Really delete this absence?', { variant: 'error', confirmLabel: 'Delete' });
    if (!yes) return;
    try { await AbsenceService.deleteAbsence(absId); await loadAbsences(); } catch (error) {
      if (error instanceof HttpError) { if (!AbsenceErrorHandler.handleDeleteError(error)) console.error('Delete error:', error); }
      else console.error('Delete error:', error);
    }
  }

  function handleToggleSelect(id: number): void {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function handleToggleSelectAll(list: Absence[]): void {
    const requestedIds = list.filter(a => a.status === 'REQUESTED').map(a => a.id);
    if (requestedIds.length === 0) return;
    const allSelected = requestedIds.every(id => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(prev => { const next = new Set(prev); requestedIds.forEach(id => next.delete(id)); return next; });
    } else {
      setSelectedIds(prev => { const next = new Set(prev); requestedIds.forEach(id => next.add(id)); return next; });
    }
  }

  async function handleBulkAction(action: 'approve' | 'reject'): Promise<void> {
    const ids = Array.from(selectedIds);
    const fn = action === 'approve' ? AbsenceService.approveAbsence : AbsenceService.rejectAbsence;
    const results = await Promise.allSettled(ids.map(id => fn(id)));
    const succeeded = results.filter(r => r.status === 'fulfilled').length;
    results.filter(r => r.status === 'rejected').forEach(r => {
      if (r.status === 'rejected' && r.reason instanceof HttpError) AbsenceErrorHandler.handleApprovalError(r.reason);
    });
    const label = action === 'approve' ? 'Approved' : 'Rejected';
    dialog.showToast({ message: `${label} ${succeeded} of ${ids.length} absences`, type: succeeded === ids.length ? 'success' : 'warning' });
    setSelectedIds(new Set());
    await loadAbsences();
  }

  function handleRowClick(abs: Absence, colName: string): void {
    if (colName === 'actions') return;
    const sTime = dateUtil.extractTimeFromISO(abs.start_datetime);
    const eTime = dateUtil.extractTimeFromISO(abs.end_datetime);
    const fds = dateUtil.isFullDayStart(sTime), fde = dateUtil.isFullDayEnd(eTime);
    setEditData({
      id: abs.id, employee: abs.employee.toString() || '',
      absence_type: abs.absence_type || 'HOLIDAY', status: abs.status || 'REQUESTED',
      start_date: dateUtil.extractDateFromISO(abs.start_datetime), full_day_start: fds,
      start_day_start_time: fds ? '09:00' : sTime, start_day_end_time: fds ? '17:00' : sTime,
      end_date: dateUtil.extractDateFromISO(abs.end_datetime), full_day_end: fde,
      end_day_start_time: fde ? '09:00' : eTime, end_day_end_time: fde ? '17:00' : eTime,
      reason_note: abs.reason_note || '',
      requested_on: abs.requested_on ? abs.requested_on.substring(0, 10) : ''
    });
    setShowEditModal(true);
  }

  function handleOpenAdd(): void {
    const preselectedEmployee = activeEmployeeId ? String(activeEmployeeId) : '';
    setAddData({
      employee: preselectedEmployee, absence_type: 'HOLIDAY',
      start_date: '', full_day_start: true, start_day_start_time: '09:00', start_day_end_time: '17:00',
      end_date: '', full_day_end: true, end_day_start_time: '09:00', end_day_end_time: '17:00',
      reason_note: '', requested_on: ''
    });
    setShowAddModal(true);
  }

  async function handleSaveAbsence(): Promise<void> {
    const { finalStart, finalEnd, isFullDay } = buildPayloadFromTimes(addData);
    const payload: AbsencePayload = {
      employee: addData.employee, absence_type: addData.absence_type,
      start_datetime: finalStart, end_datetime: finalEnd,
      reason_note: addData.reason_note || '',
      requested_on: addData.requested_on || new Date().toISOString(),
      status: 'REQUESTED', full_day: isFullDay, admin: true
    };
    try {
      await AbsenceService.createAbsence(payload);
      setShowAddModal(false);
      await loadAbsences();
    } catch (error) {
      if (error instanceof HttpError) { if (!AbsenceErrorHandler.handleCreateError(error)) { console.error('Create absence error:', error); toast.error('Error adding absence'); } }
      else { console.error('Create absence error:', error); toast.error('Error adding absence'); }
    }
  }

  async function handleUpdateModalSave(): Promise<void> {
    const { finalStart, finalEnd, isFullDay } = buildPayloadFromTimes(editData);
    const payload: AbsencePayload = {
      employee: editData.employee, absence_type: editData.absence_type,
      start_datetime: finalStart, end_datetime: finalEnd,
      reason_note: editData.reason_note || '',
      requested_on: editData.requested_on || null,
      status: editData.status, full_day: isFullDay, admin: true
    };
    if (!editData.id) return;
    try {
      await AbsenceService.updateAbsence(editData.id, payload);
      setShowEditModal(false);
      await loadAbsences();
    } catch (error) {
      if (error instanceof HttpError) { if (!AbsenceErrorHandler.handleUpdateError(error)) { console.error('Update absence error:', error); toast.error('Error updating absence'); } }
      else { console.error('Update absence error:', error); toast.error('Error updating absence'); }
    }
  }

  const empSelect = allEmployees.map(e => ({ id: e.id, text: `${e.first_name} ${e.last_name}` }));
  const sortedPage = sortAbsences(
    filteredAbsences.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    sortBy, sortDir, allEmployees,
  );

  return (
    <div className={styles.pageRoot}>
      {renderHeaderControls && (
        <>
          <div className={styles.filterBar}>
            <div className={styles.waToggleWrap}>
              {workAreas.map(wa => {
                const isSel = selectedWorkAreas.includes(wa.id);
                const isDisabled = isManager && !workAreaIds.includes(wa.id);
                return (
                  <button
                    key={wa.id}
                    onClick={() => {
                      if (isDisabled) return;
                      if (isSel) setSelectedWorkAreas(prev => prev.filter(x => x !== wa.id));
                      else setSelectedWorkAreas(prev => [...prev, wa.id]);
                    }}
                    disabled={isDisabled}
                    className={styles.waToggle}
                    style={{
                      background: isSel ? 'rgba(82, 113, 165, 0.12)' : 'rgba(255, 255, 255, 0.8)',
                      color: isDisabled ? '#94a3b8' : (isSel ? '#5271A5' : '#334155'),
                      border: isSel ? '1.5px solid #5271A5' : '1px solid rgba(0,0,0,0.08)',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      opacity: isDisabled ? 0.5 : 1,
                    }}
                  >
                    {wa.name}
                  </button>
                );
              })}
            </div>
            <div className={styles.filterActionsRight}>
              <button aria-label="Download Absences Template" title="Download Absences Template" onClick={() => downloadAbsencesTemplateXlsx()} className={styles.iconBtn}><FaDownload size={14} /></button>
              <button aria-label="Upload Absences" title="Upload Absences" onClick={() => setShowImportModal(true)} className={styles.iconBtn}><FaUpload size={14} /></button>
              <button data-testid="add-absence-button" onClick={handleOpenAdd} className={styles.addAbsenceBtn}><FaPlus style={{ fontSize: '9px' }} /> Absence</button>
            </div>
          </div>
        </>
      )}

      {/* Date Range + Status Filter */}
      <div className={styles.dateStatusRow}>
        <DateRangeFilter onRangeChange={(range) => setDateRange(range)} defaultMode="thisMonth" />
        <div className={styles.statusFilterWrap}>
          {(['REQUESTED', 'APPROVED', 'REJECTED', 'CANCELLED'] as AbsenceStatus[]).map(st => {
            const isChecked = statusFilter[st];
            const colorMap: Record<AbsenceStatus, string> = {
              REQUESTED: '#f59e0b', APPROVED: '#22c55e', REJECTED: '#ef4444', CANCELLED: '#64748b',
            };
            const color = colorMap[st];
            return (
              <button
                key={st}
                onClick={() => setStatusFilter(prev => ({ ...prev, [st]: !prev[st] }))}
                data-testid={`absence-status-filter-${st.toLowerCase()}`}
                className={styles.statusToggle}
                style={{
                  border: isChecked ? `1.5px solid ${color}` : '1px solid rgba(0,0,0,0.08)',
                  background: isChecked ? `${color}15` : 'rgba(255,255,255,0.8)',
                  color: isChecked ? color : '#64748b',
                }}
              >
                {st.toLowerCase()}
              </button>
            );
          })}
        </div>
      </div>

      {/* Active Filters Bar */}
      {renderHeaderControls && (() => {
        const activeFilters: ActiveFilter[] = [];
        if (selectedWorkAreas.length > 0) {
          activeFilters.push({ key: 'wa', label: `${selectedWorkAreas.length} Work Areas`, onClear: () => setSelectedWorkAreas([]) });
        }
        const activeStatuses = (Object.entries(statusFilter) as [AbsenceStatus, boolean][]).filter(([, v]) => v).map(([k]) => k);
        const defaultStatuses = ['REQUESTED', 'APPROVED'];
        const isNonDefault = activeStatuses.length !== defaultStatuses.length || !defaultStatuses.every(s => activeStatuses.includes(s as AbsenceStatus));
        if (isNonDefault && activeStatuses.length > 0) {
          activeFilters.push({ key: 'status', label: `${activeStatuses.length} Status`, onClear: () => setStatusFilter({ ...DEFAULT_STATUS }) });
        }
        if (dateRange) {
          const fmt = (d: Date) => d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
          activeFilters.push({ key: 'date', label: `Date: ${fmt(dateRange.start)}\u2013${fmt(dateRange.end)}`, onClear: () => setDateRange(null) });
        }
        if (activeEmployeeId) {
          const emp = allEmployees.find(e => e.id === activeEmployeeId);
          const name = emp ? `${emp.first_name} ${emp.last_name}` : `#${activeEmployeeId}`;
          activeFilters.push({ key: 'employee', label: `Employee: ${name}`, onClear: () => setActiveEmployeeId(null) });
        }
        return (
          <ActiveFiltersBar
            filters={activeFilters}
            onClearAll={() => {
              setSelectedWorkAreas([]);
              setStatusFilter({ ...DEFAULT_STATUS });
              setDateRange(null);
              setActiveEmployeeId(null);
            }}
          />
        );
      })()}

      {/* Table */}
      <div className={styles.tableScrollArea}>
        {loading && absences.length === 0 ? (
          <div className={styles.skeletonPad}><TableSkeleton columns={absenceColumns} rows={10} /></div>
        ) : (
          <AbsenceTable
            list={sortedPage}
            allEmployees={allEmployees}
            selectedIds={selectedIds}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            onToggleSelect={handleToggleSelect}
            onToggleSelectAll={handleToggleSelectAll}
            onRowClick={handleRowClick}
            onApprove={handleApprove}
            onReject={handleReject}
            onDelete={handleDeleteAbs}
          />
        )}
        <Pagination
          totalItems={filteredAbsences.length}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div data-testid="bulk-action-bar" className={styles.bulkBar}>
          <span className={styles.bulkCount}>{selectedIds.size} selected</span>
          <div className={styles.bulkActions}>
            <button data-testid="bulk-approve" onClick={() => handleBulkAction('approve')} className={styles.bulkApproveBtn}>
              <FaCheck size={10} /> Approve All
            </button>
            <button data-testid="bulk-reject" onClick={() => handleBulkAction('reject')} className={styles.bulkRejectBtn}>
              <FaTimes size={10} /> Reject All
            </button>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <AbsenceAddModal
          addData={addData}
          setAddData={setAddData}
          empSelect={empSelect}
          onClose={() => setShowAddModal(false)}
          onSave={handleSaveAbsence}
        />
      )}

      {/* Import Modal */}
      <ImportAbsencesModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onCommitted={async () => { await loadAbsences(); }}
      />

      {/* Edit Modal */}
      {showEditModal && (
        <AbsenceEditModal
          editData={editData}
          setEditData={setEditData}
          empSelect={empSelect}
          onClose={() => setShowEditModal(false)}
          onSave={handleUpdateModalSave}
        />
      )}
    </div>
  );
}
